import Language from 'models/language';
import { mapFromLanguage, mapToLanguage } from './langMapping';
import TaskTypeDTO from 'models/dto/tasks/taskTypeDTO';
import TaskType from 'models/tasks/taskType';
import { TaskType_TranslationDTO } from 'models/dto/tasks/taskType_TranslationDTO';
import { TaskType_Translation } from 'models/tasks/taskType_Translation';
import TaskTypeFormElementDTO from 'models/dto/tasks/taskTypeFormElementDTO';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { mapFromKPI } from './kpiMapping';
import { TaskTypeFormElement_TranslationDTO } from 'models/dto/tasks/taskTypeFormElement_TranslationDTO';
import { TaskTypeFormElement_Translation } from 'models/tasks/taskTypeFormElement_Translation';
import { fromApiDateTimeOptional, toApiDateTimeOptional } from 'utils/datetime';

export function mapFromTaskTypes(tasktypeDTOs: TaskTypeDTO[] | undefined): TaskType[] {
  if (!tasktypeDTOs) return [];

  const output = tasktypeDTOs.map((item) => {
    return mapFromTaskType(item);
  });

  return output;
}

export function mapFromTaskType(tasktypeDTO: TaskTypeDTO): TaskType {
  const output = new TaskType();

  output.taskTypeId = tasktypeDTO.taskTypeId;
  output.taskTypeFamilyId = tasktypeDTO.taskTypeFamilyId;
  output.version = tasktypeDTO.version;
  output.definitionHash = tasktypeDTO.definitionHash;
  output.firstUsedOn = fromApiDateTimeOptional(tasktypeDTO.firstUsedOn);
  output.lastAmendedOn = fromApiDateTimeOptional(tasktypeDTO.lastAmendedOn);
  output.rowVersion = tasktypeDTO.rowVersion;
  output.isArchived = tasktypeDTO.isArchived;
  output.authSchemaId = tasktypeDTO.authSchemaId;
  output.systemTaskType = tasktypeDTO.systemTaskType;
  output.isSystem = tasktypeDTO.isSystem;
  output.isHidden = tasktypeDTO.isHidden;
  output.trans = mapFromTaskType_Translation(tasktypeDTO.trans);
  output.form = mapFromTaskTypeForm(tasktypeDTO.form);

  if (output.trans && output.trans.length > 0) {
    output.transIdx = 0;
    output.name = output.trans[0].name;
    output.description = output.trans[0].description;
    output.tabName = output.trans[0].tabName;
  }

  return output;
}

export const mapFromTaskType_Translation = (transDTOs: TaskType_TranslationDTO[]): TaskType_Translation[] => {
  const output: TaskType_Translation[] = [];

  for (const transDTO of transDTOs) {
    const newObject_Translation = new TaskType_Translation();

    newObject_Translation.taskTypeId = transDTO.taskTypeId;
    newObject_Translation.description = transDTO.description;
    newObject_Translation.tabName = transDTO.tabName;
    newObject_Translation.name = transDTO.name;
    newObject_Translation.languageId = transDTO.languageId;
    newObject_Translation.lang = mapFromLanguage(transDTO.lang) ?? new Language();

    output.push(newObject_Translation);
  }

  return output;
};

export function mapToTaskType(taskType: TaskType): TaskTypeDTO {
  const output = new TaskTypeDTO();

  output.taskTypeId = taskType.taskTypeId;
  output.taskTypeFamilyId = taskType.taskTypeFamilyId;
  output.version = taskType.version;
  output.definitionHash = taskType.definitionHash;
  output.firstUsedOn = toApiDateTimeOptional(taskType.firstUsedOn);
  output.lastAmendedOn = toApiDateTimeOptional(taskType.lastAmendedOn);
  output.rowVersion = taskType.rowVersion;
  output.isArchived = taskType.isArchived;
  output.authSchemaId = taskType.authSchemaId;
  output.systemTaskType = taskType.systemTaskType;
  output.isHidden = taskType.isHidden;
  output.trans = mapToTaskType_Translation(taskType.trans);
  // No form means a metadata-only save: the API leaves fields unchanged for null, removes all for [].
  output.form = taskType.form ? mapToTaskTypeForm(taskType.form) : undefined;

  if (taskType.trans && taskType.transIdx >= 0) {
    output.trans[taskType.transIdx].name = taskType.name;
    output.trans[taskType.transIdx].description = taskType.description;
    output.trans[taskType.transIdx].tabName = taskType.tabName;
  }

  return output;
}

export const mapToTaskType_Translation = (transDTOs: TaskType_Translation[]): TaskType_TranslationDTO[] => {
  const output: TaskType_TranslationDTO[] = [];

  for (const transDTO of transDTOs) {
    const newObject_Translation = new TaskType_TranslationDTO();

    newObject_Translation.taskTypeId = transDTO.taskTypeId;
    newObject_Translation.description = transDTO.description;
    newObject_Translation.name = transDTO.name;
    newObject_Translation.tabName = transDTO.tabName;
    newObject_Translation.languageId = transDTO.languageId;
    newObject_Translation.lang = mapToLanguage(transDTO.lang);

    output.push(newObject_Translation);
  }

  return output;
};

export const mapFromTaskTypeFormElement = (elm: TaskTypeFormElementDTO): TaskTypeFormElement => {
  const newElm = new TaskTypeFormElement();

  newElm.kpiId = elm.kpiId;
  newElm.kpiDefinitionRevisionId = elm.kpiDefinitionRevisionId;
  newElm.kpiDefinitionRevisionRowVersion = elm.kpiDefinitionRevisionRowVersion;
  newElm.taskTypeId = elm.taskTypeId;
  newElm.fieldId = elm.fieldId;
  newElm.fieldKey = elm.fieldKey;
  newElm.fieldType = elm.fieldType;
  newElm.fieldMode = elm.fieldMode;
  newElm.kpi = elm.kpi ? mapFromKPI(elm.kpi, undefined) : undefined;
  newElm.sortOrder = elm.sortOrder;
  newElm.listId = elm.listId;
  newElm.folderId = elm.folderId;
  //server-owned, read-only here: mapToTaskTypeForm deliberately does not send these back
  newElm.entityType = elm.entityType;
  newElm.entityId = elm.entityId;
  newElm.criterionKey = elm.criterionKey;
  newElm.retiredOn = fromApiDateTimeOptional(elm.retiredOn);
  newElm.notApplicable = elm.notApplicable === true;
  newElm.trans = mapFromTaskTypeFormElement_Translation(elm.trans);

  if (newElm.trans && newElm.trans.length > 0) {
    newElm.transIdx = 0;
    newElm.name = elm.trans[0].name;
    newElm.description = elm.trans[0].description;
    newElm.expectedResult = elm.trans[0].expectedResult;
    newElm.instruction = elm.trans[0].instruction;
  }

  return newElm;
};

export const mapFromTaskTypeForm = (elements: TaskTypeFormElementDTO[] | undefined): TaskTypeFormElement[] => {
  if (!elements) return [];

  return elements.map((elm) => mapFromTaskTypeFormElement(elm));
};

export const mapToTaskTypeFormElement = (elm: TaskTypeFormElement): TaskTypeFormElementDTO => {
  const newElm = new TaskTypeFormElementDTO();

  newElm.kpiId = elm.kpiId;
  newElm.kpiDefinitionRevisionId = elm.kpiDefinitionRevisionId;
  newElm.taskTypeId = elm.taskTypeId;
  newElm.fieldId = elm.fieldId;
  newElm.fieldKey = elm.fieldKey;
  newElm.fieldType = elm.fieldType;
  newElm.fieldMode = elm.fieldMode;
  newElm.sortOrder = elm.sortOrder;
  newElm.listId = elm.listId;
  newElm.folderId = elm.folderId;
  newElm.trans = mapToTaskTypeFormElement_Translation(elm.trans);

  if (elm.trans && elm.transIdx >= 0) {
    newElm.trans[elm.transIdx].name = elm.name;
    newElm.trans[elm.transIdx].description = elm.description;
    newElm.trans[elm.transIdx].expectedResult = elm.expectedResult;
    newElm.trans[elm.transIdx].instruction = elm.instruction;
  }

  return newElm;
};

export const mapToTaskTypeForm = (elements: TaskTypeFormElement[] | undefined): TaskTypeFormElementDTO[] => {
  if (!elements) return [];

  return elements.map((elm) => mapToTaskTypeFormElement(elm));
};

export const mapFromTaskTypeFormElement_Translation = (
  transDTOs: TaskTypeFormElement_TranslationDTO[],
): TaskTypeFormElement_Translation[] => {
  const output: TaskTypeFormElement_Translation[] = [];

  for (const transDTO of transDTOs) {
    const newObject_Translation = new TaskTypeFormElement_Translation();

    newObject_Translation.taskTypeId = transDTO.taskTypeId;
    newObject_Translation.description = transDTO.description;
    newObject_Translation.name = transDTO.name;
    newObject_Translation.expectedResult = transDTO.expectedResult;
    newObject_Translation.instruction = transDTO.instruction;
    newObject_Translation.languageId = transDTO.languageId;
    newObject_Translation.lang = mapFromLanguage(transDTO.lang) ?? new Language();

    output.push(newObject_Translation);
  }

  return output;
};

export const mapToTaskTypeFormElement_Translation = (
  transDTOs: TaskTypeFormElement_Translation[],
): TaskTypeFormElement_TranslationDTO[] => {
  const output: TaskTypeFormElement_TranslationDTO[] = [];

  for (const transDTO of transDTOs) {
    const newObject_Translation = new TaskTypeFormElement_TranslationDTO();

    newObject_Translation.taskTypeId = transDTO.taskTypeId;
    newObject_Translation.description = transDTO.description;
    newObject_Translation.name = transDTO.name;
    newObject_Translation.expectedResult = transDTO.expectedResult;
    newObject_Translation.instruction = transDTO.instruction;
    newObject_Translation.languageId = transDTO.languageId;
    newObject_Translation.lang = mapToLanguage(transDTO.lang);

    output.push(newObject_Translation);
  }

  return output;
};
